/**
 * Composer API — mock implementation.
 *
 * Everything here runs in-browser with no real network calls. The real
 * implementation will hit:
 *   GET  /api/v1/accounts            → ConnectedAccount[]
 *   GET  /api/v1/drafts              → ComposerDraft[]
 *   POST /api/v1/drafts              → ComposerDraft
 *   PUT  /api/v1/drafts/:id          → ComposerDraft
 *   DELETE /api/v1/drafts/:id        → 204
 *   POST /api/v1/posts               → { postId } (immediate publish)
 *   POST /api/v1/posts/schedule      → { postId } (scheduled publish)
 */

import { findLibraryFile } from './mediaTree.api';
import type { MediaLibraryFile } from './mediaTree.api';
import type { PlatformId } from '../config/platforms';
import type {
  ConnectedAccount,
  ComposerDraft,
  ComposerMedia,
  PerPlatformResult,
  PerPlatformStatus,
} from '../types/composer';

// ─── Mock connected accounts ─────────────────────────────────────────────────

/** One account per platform, matching the design's platform grid. */
const MOCK_ACCOUNTS: ConnectedAccount[] = [
  { id: 'fb-1', platform: 'facebook', name: 'Page A', avatarUrl: null, connected: true },
  { id: 'ig-1', platform: 'instagram', name: 'Business', avatarUrl: null, connected: true },
  { id: 'li-1', platform: 'linkedin', name: 'Company Page', avatarUrl: null, connected: true },
  { id: 'x-1', platform: 'x', name: '@johndoe', avatarUrl: null, connected: true },
  { id: 'yt-1', platform: 'youtube', name: 'Channel', avatarUrl: null, connected: true },
  { id: 'tt-1', platform: 'tiktok', name: '@johndoe', avatarUrl: null, connected: true },
  { id: 'th-1', platform: 'threads', name: '@johndoe', avatarUrl: null, connected: true },
  { id: 'pin-1', platform: 'pinterest', name: 'Profile', avatarUrl: null, connected: true },
];

/** Selected on first render, so the composer opens looking like the design. */
export const MOCK_SELECTED_ACCOUNT_IDS = ['fb-1', 'ig-1', 'li-1', 'x-1', 'yt-1'];

export const MOCK_POST_TEXT = [
  'Excited to launch our new product! 🎉',
  '',
  "It's faster, smarter, and built to help you achieve more.",
  '',
  'Check it out and let us know what you think! 👇',
  '',
  '#NewLaunch #Innovation #Productivity',
].join('\n');

export async function listConnectedAccounts(): Promise<ConnectedAccount[]> {
  await delay(300);
  return [...MOCK_ACCOUNTS];
}

// ─── Mock attached media ─────────────────────────────────────────────────────

/**
 * The three attachments the composer opens with, taken straight out of the
 * library tree so the picker shows them already selected. Building them here
 * from separate artwork would let the two drift and show the same file with two
 * different thumbnails.
 */
export function buildMockMedia(): ComposerMedia[] {
  return ['f-launch-1', 'f-launch-2', 'f-launch-4']
    .map(findLibraryFile)
    .filter((file): file is MediaLibraryFile => file !== null)
    .map((file) => ({
      id: `lib-${file.id}`,
      file: null,
      libraryId: file.id,
      name: file.name,
      sizeBytes: file.sizeBytes,
      previewUrl: file.previewUrl,
      type: file.type,
      width: file.width,
      height: file.height,
      durationMs: file.durationMs,
      uploadPercent: 100,
      uploaded: true,
      uploadError: null,
    }));
}

// ─── Draft storage (localStorage) ────────────────────────────────────────────

const DRAFT_KEY = 'composer_drafts_v1';

function readDrafts(): ComposerDraft[] {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? (JSON.parse(raw) as ComposerDraft[]) : [];
  } catch {
    return [];
  }
}

function writeDrafts(drafts: ComposerDraft[]): void {
  localStorage.setItem(DRAFT_KEY, JSON.stringify(drafts));
}

export async function listDrafts(): Promise<ComposerDraft[]> {
  await delay(200);
  return readDrafts();
}

export async function saveDraft(
  draft: Omit<ComposerDraft, 'id' | 'savedAt'> & { id?: string }
): Promise<ComposerDraft> {
  await delay(150);
  const drafts = readDrafts();
  const now = new Date().toISOString();

  if (draft.id) {
    const idx = drafts.findIndex((d) => d.id === draft.id);
    const updated: ComposerDraft = { ...draft, id: draft.id, savedAt: now };
    if (idx >= 0) {
      drafts[idx] = updated;
    } else {
      drafts.unshift(updated);
    }
    writeDrafts(drafts);
    return updated;
  }

  const created: ComposerDraft = {
    ...draft,
    id: crypto.randomUUID(),
    savedAt: now,
  };
  drafts.unshift(created);
  writeDrafts(drafts);
  return created;
}

export async function deleteDraft(draftId: string): Promise<void> {
  await delay(100);
  writeDrafts(readDrafts().filter((d) => d.id !== draftId));
}

export async function duplicateDraft(draftId: string): Promise<ComposerDraft> {
  await delay(100);
  const drafts = readDrafts();
  const source = drafts.find((d) => d.id === draftId);
  if (!source) throw new Error('Draft not found');
  const copy: ComposerDraft = {
    ...source,
    id: crypto.randomUUID(),
    savedAt: new Date().toISOString(),
  };
  drafts.unshift(copy);
  writeDrafts(drafts);
  return copy;
}

// ─── Publish simulation ──────────────────────────────────────────────────────

export type PublishProgressCallback = (results: PerPlatformResult[]) => void;

/**
 * Simulates publishing to each selected account independently.
 * Each account progresses through its own lifecycle asynchronously.
 * Calls `onProgress` after every state change.
 */
export async function publishPost(
  accountIds: string[],
  _text: string,
  onProgress: PublishProgressCallback
): Promise<PerPlatformResult[]> {
  const accounts = MOCK_ACCOUNTS.filter((a) => accountIds.includes(a.id));

  const results: PerPlatformResult[] = accounts.map((a) => ({
    accountId: a.id,
    platform: a.platform,
    accountName: a.name,
    status: 'queued' as PerPlatformStatus,
    error: null,
  }));

  onProgress([...results]);

  // Run each account publish concurrently
  const tasks = results.map(async (r, i) => {
    // Stagger starts slightly so progress doesn't look synchronised
    await delay(i * 180);

    results[i] = { ...r, status: 'uploading' };
    onProgress([...results]);
    await delay(800 + Math.random() * 600);

    results[i] = { ...results[i], status: 'publishing' };
    onProgress([...results]);
    await delay(700 + Math.random() * 800);

    // Simulate a 10% failure rate per account
    const failed = Math.random() < 0.1;
    results[i] = {
      ...results[i],
      status: failed ? 'failed' : 'success',
      error: failed ? 'Simulated API error — retry to try again' : null,
    };
    onProgress([...results]);
  });

  await Promise.all(tasks);
  return results;
}

/**
 * Retry a single previously-failed account publish.
 */
export async function retryPublish(
  accountId: string,
  results: PerPlatformResult[],
  onProgress: PublishProgressCallback
): Promise<PerPlatformResult[]> {
  const updated = results.map((r) =>
    r.accountId === accountId ? { ...r, status: 'queued' as PerPlatformStatus, error: null } : r
  );
  onProgress([...updated]);
  await delay(200);

  const idx = updated.findIndex((r) => r.accountId === accountId);
  updated[idx] = { ...updated[idx], status: 'uploading' };
  onProgress([...updated]);
  await delay(900);

  updated[idx] = { ...updated[idx], status: 'publishing' };
  onProgress([...updated]);
  await delay(900);

  updated[idx] = { ...updated[idx], status: 'success', error: null };
  onProgress([...updated]);

  return updated;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ─── Platform labels for accounts ────────────────────────────────────────────

export function getPlatformLabel(platform: PlatformId): string {
  const labels: Record<PlatformId, string> = {
    facebook: 'Facebook',
    instagram: 'Instagram',
    linkedin: 'LinkedIn',
    youtube: 'YouTube',
    x: 'X (Twitter)',
    threads: 'Threads',
    tiktok: 'TikTok',
    pinterest: 'Pinterest',
  };
  return labels[platform];
}
